import { com, kotlin } from '@eidu/web'
import FieldData from './FieldData'
import { BatchFieldErrors, fieldValuesFromStringsBatch } from './FieldValueUtil'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import validateFieldValuesBatch = com.eidu.sharedlib.entity.validation.validateFieldValuesBatch
import KtList = kotlin.collections.KtList
import KtMap = kotlin.collections.KtMap
import ReferenceValue = com.eidu.sharedlib.entity.field.ReferenceValue
import EntityValidationError = com.eidu.sharedlib.entity.validation.EntityValidationError
import Outcome = com.eidu.sharedlib.util.Outcome
import Failure = com.eidu.sharedlib.util.Failure
import Success = com.eidu.sharedlib.util.Success

export type { BatchFieldErrors }

const hasErrors = (results: BatchFieldErrors) => results.some((entityErrors) => entityErrors.size > 0)

const createValidatedFieldValues = async (
  data: readonly (readonly string[])[],
  fields: readonly FieldData[],
  getTypeIds: (ids: EntityId[]) => Promise<ReadonlyMap<EntityId, EntityTypeId>>
): Promise<Outcome<BatchFieldErrors, readonly Map<FieldId, FieldValue>[]>> => {
  const numColumns = Math.max(...data.map((row) => row.length))
  if (fields.length !== numColumns) throw Error('Number of columns mismatch')

  const valuesOutcome = fieldValuesFromStringsBatch(data, fields)
  if (valuesOutcome instanceof Failure) {
    return valuesOutcome
  }

  const fieldValues = (valuesOutcome as Success<readonly Map<FieldId, FieldValue>[]>).value
  if (!fieldValues.length) throw Error('No entities found')

  const referencedIds = fieldValues.flatMap((it) =>
    Array.from(it.values()).flatMap((value) => (value instanceof ReferenceValue ? [value.value] : []))
  )
  const typeIds = await getTypeIds(referencedIds)
  const getTypeId = async (id: EntityId) => typeIds.get(id)

  const fieldsOnly = fields.map((it) => it.field)

  const validationResults = await validateFieldValuesBatch(
    KtList.fromJsArray(fieldValues.map((it) => KtMap.fromJsMap(it))),
    KtList.fromJsArray(fieldsOnly),
    getTypeId
  )

  const batchErrors: BatchFieldErrors = validationResults
    .asJsReadonlyArrayView()
    .map(
      (ktMap) =>
        new EqualityHashMap<FieldId, readonly EntityValidationError[]>(
          [...ktMap.asJsReadonlyMapView().entries()].map(([k, v]) => [k, [...v.asJsReadonlyArrayView()]])
        )
    )

  if (hasErrors(batchErrors)) {
    return new Failure(batchErrors)
  } else {
    return new Success(fieldValues)
  }
}

export default createValidatedFieldValues
